import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Eye, EyeOff, Check } from 'lucide-react';
import { toast } from 'sonner';

const ChangePassword: React.FC = () => {
  const { t } = useTranslation(['auth', 'common', 'errors', 'validation']);
  const navigate = useNavigate();
  const { changePassword, user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validatePasswords = () => {
    if (!currentPassword) {
      toast.error(t('validation:required'));
      return false;
    }
    
    if (!newPassword) {
      toast.error(t('validation:required'));
      return false;
    }
    
    if (newPassword.length < 8) {
      toast.error(t('validation:min_length', { min: 8 }));
      return false;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error(t('auth:passwordMismatch'));
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePasswords()) {
      return;
    }

    try {
      setLoading(true);
      await changePassword(currentPassword, newPassword);
      
      // Navigate based on user role
      if (user?.role === 'superadmin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error: any) {
      // C21-N7: Map known backend messages to semantic codes, never render raw text.
      const detail = error?.response?.data?.detail || '';
      if (error?.code === 'auth:passwordResetRequired') {
        toast.error(t('auth:passwordResetRequired'));
      } else if (error?.code === 'auth:newPasswordRequired') {
        toast.error(t('auth:newPasswordRequired'));
      } else if (/current\s*password\s*(is\s*)?incorrect/i.test(detail)) {
        toast.error(t('auth:currentPasswordIncorrect'));
      } else {
        toast.error(t('auth:passwordChangeFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-xl shadow-lg">
        
        {/* Header */}
        <div className="text-center">
          <Lock className="mx-auto h-12 w-12 text-blue-600" />
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            {t('auth:changePassword.title')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('auth:changePassword.description')}
          </p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          
          {/* Current Password */}
          <div>
            <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth:changePassword.currentPassword')}
            </label>
            <div className="relative">
              <input
                id="current-password"
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('auth:changePassword.currentPasswordPlaceholder')}
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              >
                {showCurrentPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth:changePassword.newPassword')}
            </label>
            <div className="relative">
              <input
                id="new-password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('auth:changePassword.newPasswordPlaceholder')}
                required
                minLength={8}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth:changePassword.confirmPassword')}
            </label>
            <div className="relative">
              <input
                id="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('auth:changePassword.confirmPasswordPlaceholder')}
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Password requirements */}
          <div className="text-sm text-gray-600">
            <p className="mb-2">{t('auth:changePassword.requirements')}</p>
            <ul className="space-y-1 text-xs">
              <li className={`flex items-center ${newPassword.length >= 8 ? 'text-green-600' : 'text-gray-400'}`}>
                <Check className="w-3 h-3 mr-1" />
                {t('auth:changePassword.minLength')}
              </li>
              <li className={`flex items-center ${/[A-Z]/.test(newPassword) ? 'text-green-600' : 'text-gray-400'}`}>
                <Check className="w-3 h-3 mr-1" />
                {t('auth:changePassword.uppercase')}
              </li>
              <li className={`flex items-center ${/[0-9]/.test(newPassword) ? 'text-green-600' : 'text-gray-400'}`}>
                <Check className="w-3 h-3 mr-1" />
                {t('auth:changePassword.number')}
              </li>
            </ul>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('auth:changePassword.changing')}
              </>
            ) : (
              <>
                <Lock className="mr-2 w-4 h-4" />
                {t('auth:changePassword.submit')}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;